import {
  CalendarX2,
  CornerDownLeft,
  GraduationCap,
  ListChecks,
  Package2,
  Play,
  QrCode,
  RefreshCw,
  Settings,
  ShoppingCart,
  SlidersHorizontal,
  Store,
  Truck,
} from 'lucide-react'
import { type ElementType, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { SheetsDiagnosticsPanel } from '@/diagnostics/SheetsDiagnosticsPanel'
import { useSecretTap } from '@/hooks/use-secret-tap'
import { cn } from '@/lib/utils'
import { useLessonProgress } from '@/player/lesson-progress'
import { getSessionIdentity } from '@/session/identity'
import { roleDisplayName } from '@/session/role-labels'
import { type ContentCatalogue, useContentCatalogue } from './content-catalogue'
import { deriveDashboard, type ModuleCardModel } from './derive'
import { RequiredLessonCard } from './required-lesson-card'

// BOOT-02/BOOT-03: identity labels absence is explicit, never silently defaulted.
const UNIDENTIFIED_ID_LABEL = 'Not identified'
const UNIDENTIFIED_ROLE_LABEL = "Progress won't be saved"
// Shown when EmployeeId was sent but Role was not.
const MISSING_ROLE_LABEL = 'Role missing'

function roleDisplay(roleId: string): string {
  return roleId === '' ? MISSING_ROLE_LABEL : roleDisplayName(roleId)
}

const ICON_MAP: Record<string, ElementType> = {
  ShoppingCart,
  Truck,
  Package2,
  CornerDownLeft,
  SlidersHorizontal,
  CalendarX2,
  ListChecks,
  QrCode,
}

const ACCENT_BG: Record<string, string> = {
  blue: 'bg-blue-100/80 text-[#2563eb]',
  amber: 'bg-amber-100/80 text-amber-700',
  indigo: 'bg-indigo-100/80 text-indigo-600',
  rose: 'bg-rose-100/80 text-rose-600',
  purple: 'bg-purple-100/80 text-purple-600',
  red: 'bg-red-100/80 text-red-600',
  teal: 'bg-teal-100/80 text-teal-600',
}

function resolveIcon(name: string): ElementType {
  const Icon = ICON_MAP[name]
  if (Icon !== undefined) return Icon
  if (import.meta.env.DEV) {
    console.warn(
      `[DashboardScreen] No icon registered for "${name}" falling back to GraduationCap. Add the module to MODULE_REGISTRY.`,
    )
  }
  return GraduationCap
}

function resolveAccent(name: string): string {
  return ACCENT_BG[name] ?? 'bg-slate-100 text-slate-600'
}

const EMPTY_CATALOGUE: ContentCatalogue = []
const UNKNOWN_COUNT = '…'

function LoadingNote({ label }: Readonly<{ label: string }>) {
  return <p className="text-sm text-slate-400 py-2">{label}</p>
}

const STATUS_COUNT_CLASS: Record<string, string> = {
  done: 'text-emerald-600 font-semibold',
  'in-progress': 'text-[#2563eb] font-semibold',
}

const STATUS_ACTION_LABEL: Record<string, string> = {
  done: 'Review Practice',
  'in-progress': 'Continue',
}

function ModuleCard({ mod, onPress }: Readonly<{ mod: ModuleCardModel; onPress: () => void }>) {
  const Icon = resolveIcon(mod.icon)

  const countLabel = (() => {
    switch (mod.status) {
      case 'done':
        return 'Done'
      case 'in-progress':
        return `${mod.progress} of ${mod.total}`
      default:
        return `${mod.progress}/${mod.total}`
    }
  })()

  const countClass = STATUS_COUNT_CLASS[mod.status] ?? 'text-slate-400 font-medium'

  const barWidth = mod.status === 'done' ? 100 : mod.percent
  const barColour = mod.status === 'done' ? 'bg-emerald-500' : 'bg-[#2563eb]'

  const noLessons = mod.total === 0 || mod.comingSoon
  const ActionIcon = mod.status === 'done' ? RefreshCw : Play
  const actionLabel = STATUS_ACTION_LABEL[mod.status] ?? 'Start'
  const actionClass =
    mod.status === 'done'
      ? 'bg-slate-100 text-slate-600 border border-slate-200'
      : 'bg-[#2563eb] text-white'

  return (
    <button
      type="button"
      onClick={noLessons ? undefined : onPress}
      disabled={noLessons}
      className="w-44 shrink-0 p-3.5 rounded-2xl bg-white border border-slate-200/80 flex flex-col gap-2.5 shadow-xs text-left active:scale-[0.97] transition-transform touch-manipulation animate-scroll-reveal-h disabled:opacity-60 disabled:active:scale-100"
    >
      <div
        className={cn(
          'w-9 h-9 rounded-xl flex items-center justify-center',
          resolveAccent(mod.accentColour),
        )}
      >
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-bold text-[#131b2e] leading-snug">{mod.title}</h3>
        <p className="text-sm text-slate-600 leading-snug mt-0.5">{mod.subtitle}</p>
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-1">
          <span className="text-sm text-slate-600">Lessons</span>
          <span className={cn('text-sm', countClass)}>{countLabel}</span>
        </div>
        {mod.status === 'in-progress' && (
          <span className="text-sm font-semibold text-[#2563eb]">{mod.percent}%</span>
        )}
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all', barColour)}
            style={{ width: `${barWidth}%` }}
          />
        </div>
      </div>
      {noLessons ? (
        <div className="w-full py-2 rounded-xl text-sm font-semibold flex items-center justify-center bg-slate-100 text-slate-400 border border-slate-200">
          Coming Soon
        </div>
      ) : (
        <div
          className={cn(
            'w-full py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5',
            actionClass,
          )}
        >
          <ActionIcon className="w-3 h-3" />
          {actionLabel}
        </div>
      )}
    </button>
  )
}

function StickyProgressHeader({
  scrolled,
  identity,
  loading,
  completeModules,
  totalModules,
  lessonPercent,
  overallPercent,
}: Readonly<{
  scrolled: boolean
  identity: { employeeId: string } | null
  loading: boolean
  completeModules: number
  totalModules: number
  lessonPercent: number
  overallPercent: number
}>) {
  return (
    <div
      aria-hidden={!scrolled}
      className={cn(
        'absolute top-0 inset-x-0 z-20',
        !scrolled && 'pointer-events-none',
        'transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
        scrolled ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0',
      )}
    >
      <div className="bg-primary/96 backdrop-blur-md border-b border-white/10 px-4 py-2.5 shadow-xl flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-sm font-bold text-white truncate">
              {identity ? identity.employeeId : UNIDENTIFIED_ID_LABEL}
            </span>
            <span className="text-white/30 text-xs">·</span>
            <span className="text-xs text-white/60">
              {loading ? '' : `${completeModules}/${totalModules} modules`}
            </span>
          </div>
          <div className="h-1 rounded-full bg-white/20 overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-700 ease-out"
              style={{ width: scrolled ? `${lessonPercent}%` : '0%' }}
            />
          </div>
        </div>
        <div className="shrink-0 text-right">
          <span className="text-xl font-bold text-white leading-none block">
            {loading ? '' : `${overallPercent}%`}
          </span>
          <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest">
            Overall
          </span>
        </div>
      </div>
    </div>
  )
}

function HeroBanner({
  identity,
  onSettingsTap,
  onSecretTap,
}: Readonly<{
  identity: { employeeId: string; roleId: string } | null
  onSettingsTap: () => void
  onSecretTap: () => void
}>) {
  return (
    <div className="bg-primary pt-8 pb-16 px-4">
      <div className="flex items-start justify-between animate-dash-down">
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={onSecretTap}
            className="text-xl font-bold text-white tracking-tight leading-none text-left"
          >
            {identity ? identity.employeeId : UNIDENTIFIED_ID_LABEL}
          </button>
          <div className="flex items-center gap-1.5">
            <Store className="w-3.5 h-3.5 text-white/60 shrink-0" />
            <span
              className={cn(
                'text-sm font-medium leading-none',
                identity ? 'text-white/75' : 'text-amber-200',
              )}
            >
              {identity ? roleDisplay(identity.roleId) : UNIDENTIFIED_ROLE_LABEL}
            </span>
          </div>
        </div>
        <button
          type="button"
          aria-label="Accessibility settings"
          data-app-tour="accessibility-btn"
          onClick={onSettingsTap}
          className="w-9 h-9 rounded-full bg-white/90 text-[#2563eb] flex items-center justify-center shadow-sm active:scale-[0.97] transition-transform shrink-0"
        >
          <Settings className="w-4.5 h-4.5" />
        </button>
      </div>
    </div>
  )
}

export function DashboardScreen() {
  const navigate = useNavigate()
  const catalogue = useContentCatalogue()
  const stages = useLessonProgress((s) => s.stages)
  const model = useMemo(
    () =>
      deriveDashboard(catalogue ?? EMPTY_CATALOGUE, stages, {
        roleId: getSessionIdentity()?.roleId ?? null,
      }),
    [catalogue, stages],
  )
  const [scrolled, setScrolled] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => setScrolled(el.scrollTop > 140)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  // Five quick taps on the employee ID opens the Sheets diagnostics panel.
  const secret = useSecretTap()
  const identity = getSessionIdentity()

  const loading = catalogue === null

  // Module-level counts for the banner (distinct from lesson counts).
  const completeModules = model.modules.filter((m) => m.status === 'done').length
  const totalModules = model.modules.length

  const lessonPercent = loading ? 0 : model.overall.percent

  return (
    <div className="h-full flex flex-col bg-[#f3f5fa] relative overflow-hidden">
      {secret.revealed && <SheetsDiagnosticsPanel onClose={secret.hide} />}

      {/* Compact sticky progress header slides in after hero scrolls away */}
      <StickyProgressHeader
        scrolled={scrolled}
        identity={identity}
        loading={loading}
        completeModules={completeModules}
        totalModules={totalModules}
        lessonPercent={lessonPercent}
        overallPercent={model.overall.percent}
      />

      {/* Single scroll container hero and content move together */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden"
        style={{ overscrollBehavior: 'contain' }}
      >
        {/* Blue header */}
        <HeroBanner
          identity={identity}
          onSettingsTap={() => void navigate('/accessibility')}
          onSecretTap={secret.onTap}
        />

        {/* White card overlaps hero bottom, scrolls with it */}
        <div
          className="-mt-10 rounded-t-[28px] bg-card px-4 pt-6 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] space-y-6 animate-dash-up min-h-full"
          style={{ animationDelay: '200ms' }}
        >
          {/* Progress summary card */}
          <div
            data-app-tour="progress-card"
            className="bg-primary rounded-2xl p-3.5 animate-dash-scale"
            style={{ animationDelay: '130ms' }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-sm font-bold text-white leading-snug">
                  {loading ? 'Loading…' : `${completeModules} of ${totalModules} Modules Complete`}
                </span>
                <span className="text-xs text-white/65">
                  {loading
                    ? 'Loading lessons…'
                    : `${model.overall.completeLessons} of ${model.overall.totalLessons} Lessons Done`}
                </span>
              </div>
              <div className="flex flex-col items-end shrink-0">
                <span className="text-2xl font-bold text-white leading-none">
                  {loading ? UNKNOWN_COUNT : `${model.overall.percent}%`}
                </span>
                <span className="text-[10px] font-bold text-white/55 uppercase tracking-widest mt-0.5">
                  Overall Progress
                </span>
              </div>
            </div>

            <div className="mt-3 flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/65">Lesson Progress</span>
                <span className="text-xs font-bold text-white/80">{lessonPercent}%</span>
              </div>
              <div aria-hidden="true" className="h-1.5 rounded-full bg-black/20 overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full animate-progress-fill"
                  style={
                    {
                      '--target-w': `${lessonPercent}%`,
                      animationDelay: '420ms',
                    } as React.CSSProperties
                  }
                />
              </div>
              <progress
                value={lessonPercent}
                max={100}
                aria-label={
                  loading
                    ? 'Overall progress'
                    : `Overall progress: ${model.overall.percent}% — ${model.overall.completeLessons} of ${model.overall.totalLessons} Lessons Done`
                }
                className="sr-only"
              />
            </div>
          </div>

          {/* Operational Modules */}
          <section data-app-tour="modules-section" className="flex flex-col space-y-3">
            <h2
              className="text-xl font-bold text-foreground animate-dash-down"
              style={{ animationDelay: '260ms' }}
            >
              Operational Modules
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-none">
              {loading ? (
                <LoadingNote label="Loading modules…" />
              ) : (
                model.modules.map((mod) => (
                  <ModuleCard
                    key={mod.id}
                    mod={mod}
                    onPress={() => navigate(`/module/${mod.id}`)}
                  />
                ))
              )}
            </div>
          </section>

          {/* Required Training */}
          <section className="flex flex-col space-y-3">
            <div
              className="flex items-center gap-2 animate-dash-down"
              style={{ animationDelay: '360ms' }}
            >
              <h2 className="text-xl font-bold text-foreground">Required Training</h2>
              <span className="w-5 h-5 rounded-full bg-[#2563eb] text-white text-xs font-bold flex items-center justify-center shrink-0">
                {loading ? UNKNOWN_COUNT : model.requiredLessons.length}
              </span>
            </div>
            <div className="-mx-4">
              {loading ? (
                <LoadingNote label="Loading required training…" />
              ) : (
                model.requiredLessons.slice(0, 8).map((item, i) => (
                  <div key={item.id}>
                    {i > 0 && <div className="h-px bg-slate-200 mx-4" />}
                    <RequiredLessonCard
                      item={item}
                      onPress={() => navigate(`/lesson-detail/${item.id}`)}
                    />
                  </div>
                ))
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate('/required-training')}
              className="w-full py-3 px-4 rounded-xl text-black active:scale-[0.99] text-base font-semibold border border-black transition-all flex items-center justify-center gap-1.5"
            >
              See all required training
            </button>
          </section>
        </div>
      </div>
    </div>
  )
}
